/**
 * Query Transformation Utilities
 *
 * This module provides utilities for converting between different query formats
 * used in the CYM Dashboard search functionality:
 *
 * 1. String-based queries (e.g., "status:error AND host:server1")
 * 2. Elasticsearch DSL JSON format (e.g., {"query": {"bool": {...}}})
 *
 * The transformations are bidirectional and support:
 * - Basic field:value queries
 * - Boolean logic (AND, OR, NOT)
 * - Range queries (>=, <=, >, <)
 * - Wildcard queries
 * - Aggregations
 * - Index specifications
 *
 * These utilities enable seamless integration between the traditional
 * string-based search interface and the new Monaco Editor JSON interface.
 */

/**
 * Converts a JSON Elasticsearch DSL query to a query string format
 * @param {Object} jsonQuery - The Elasticsearch DSL query object
 * @returns {string} - The converted query string
 */
export function jsonToQueryString(jsonQuery) {
  if (!jsonQuery || typeof jsonQuery !== 'object') {
    return ''
  }

  try {
    let queryString = ''
    
    // Extract index from query if present
    if (jsonQuery.index) {
      queryString += `index:${jsonQuery.index} `
    }

    // Process the main query
    if (jsonQuery.query) {
      const queryPart = processQuery(jsonQuery.query)
      if (queryPart) {
        queryString += queryPart
      }
    }

    // Process aggregations
    if (jsonQuery.aggs || jsonQuery.aggregations) {
      const aggs = jsonQuery.aggs || jsonQuery.aggregations
      const aggPart = processAggregations(aggs)
      if (aggPart) {
        queryString += (queryString ? ' | ' : '') + aggPart
      }
    }

    return queryString.trim()
  } catch (error) {
    console.error('Error converting JSON to query string:', error)
    return ''
  }
}

/**
 * Converts a query string to Elasticsearch DSL JSON format
 * @param {string} queryString - The query string to convert
 * @returns {Object} - The converted Elasticsearch DSL query object
 */
export function queryStringToJson(queryString) {
  if (!queryString || typeof queryString !== 'string') {
    return { query: { match_all: {} } }
  }

  try {
    const query = { query: { match_all: {} } }
    
    // Split query and aggregation parts
    const parts = queryString.split('|')
    const queryPart = parts[0]?.trim()
    const aggPart = parts[1]?.trim()

    // Process query part
    if (queryPart) {
      const parsedQuery = parseQueryString(queryPart)
      if (parsedQuery) {
        query.query = parsedQuery
      }
    }

    // Process aggregation part
    if (aggPart) {
      const parsedAggs = parseAggregationString(aggPart)
      if (parsedAggs) {
        query.aggs = parsedAggs
      }
    }

    return query
  } catch (error) {
    console.error('Error converting query string to JSON:', error)
    return { query: { match_all: {} } }
  }
}

/**
 * Validates an Elasticsearch DSL query against the schema
 * @param {Object} query - The query object to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
export function validateElasticsearchQuery(query) {
  const result = {
    isValid: true,
    errors: []
  }

  try {
    if (!query || typeof query !== 'object') {
      result.isValid = false
      result.errors.push('Query must be a valid JSON object')
      return result
    }

    // Basic structure validation
    if (!query.query && !query.aggs && !query.aggregations) {
      result.isValid = false
      result.errors.push('Query must contain at least a "query" or "aggs"/"aggregations" field')
      return result
    }

    // Validate query structure
    if (query.query) {
      const queryValidation = validateQueryStructure(query.query)
      if (!queryValidation.isValid) {
        result.isValid = false
        result.errors.push(...queryValidation.errors)
      }
    }

    // Validate aggregations structure
    if (query.aggs || query.aggregations) {
      const aggsValidation = validateAggregationsStructure(query.aggs || query.aggregations)
      if (!aggsValidation.isValid) {
        result.isValid = false
        result.errors.push(...aggsValidation.errors)
      }
    }

    return result
  } catch (error) {
    result.isValid = false
    result.errors.push(`Validation error: ${error.message}`)
    return result
  }
}

/**
 * Extracts index name from an Elasticsearch DSL query
 * @param {Object} query - The Elasticsearch DSL query object
 * @returns {string|null} - The extracted index name or null if not found
 */
export function extractIndexFromQuery(query) {
  if (!query || typeof query !== 'object') {
    return null
  }

  // Check for explicit index field
  if (query.index) {
    return query.index
  }

  // Check for index in query string format within the query
  if (query.query && query.query.query_string && query.query.query_string.query) {
    const queryStr = query.query.query_string.query
    const indexMatch = queryStr.match(/index:(\w+)/)
    if (indexMatch) {
      return indexMatch[1]
    }
  }

  return null
}

// Helper functions

function processQuery(query) {
  if (!query) return ''

  if (query.match_all) {
    return '*'
  }

  if (query.match) {
    const field = Object.keys(query.match)[0]
    const value = query.match[field]
    if (typeof value === 'string') {
      return `${field}:${value}`
    } else if (value.query) {
      return `${field}:${value.query}`
    }
  }

  if (query.term) {
    const field = Object.keys(query.term)[0]
    const value = query.term[field]
    if (typeof value === 'object' && value.value !== undefined) {
      return `${field}:${value.value}`
    }
    return `${field}:${value}`
  }

  if (query.terms) {
    const field = Object.keys(query.terms)[0]
    const values = query.terms[field]
    return `${field}:(${values.join(' OR ')})`
  }

  if (query.range) {
    const field = Object.keys(query.range)[0]
    const range = query.range[field]
    let rangeStr = `${field}:`
    if (range.gte !== undefined) rangeStr += `>=${range.gte}`
    if (range.gt !== undefined) rangeStr += `>${range.gt}`
    if (range.lte !== undefined) rangeStr += ` <=${range.lte}`
    if (range.lt !== undefined) rangeStr += ` <${range.lt}`
    return rangeStr
  }

  if (query.bool) {
    const parts = []
    
    if (query.bool.must) {
      const mustParts = query.bool.must.map(q => processQuery(q)).filter(Boolean)
      if (mustParts.length > 0) {
        parts.push(mustParts.join(' AND '))
      }
    }

    if (query.bool.should) {
      const shouldParts = query.bool.should.map(q => processQuery(q)).filter(Boolean)
      if (shouldParts.length > 0) {
        parts.push(`(${shouldParts.join(' OR ')})`)
      }
    }

    if (query.bool.must_not) {
      const mustNotParts = query.bool.must_not.map(q => processQuery(q)).filter(Boolean)
      if (mustNotParts.length > 0) {
        parts.push(`NOT (${mustNotParts.join(' OR ')})`)
      }
    }

    return parts.join(' AND ')
  }

  if (query.wildcard) {
    const field = Object.keys(query.wildcard)[0]
    const value = query.wildcard[field]
    if (typeof value === 'object' && value.value !== undefined) {
      return `${field}:${value.value}`
    }
    return `${field}:${value}`
  }

  if (query.query_string) {
    return query.query_string.query
  }

  return ''
}

function processAggregations(aggs) {
  if (!aggs || typeof aggs !== 'object') return ''

  const aggNames = Object.keys(aggs)
  if (aggNames.length === 0) return ''

  const firstAgg = aggs[aggNames[0]]
  
  if (firstAgg.terms) {
    return `top by ${firstAgg.terms.field}`
  }

  if (firstAgg.date_histogram) {
    return `timechart by ${firstAgg.date_histogram.field}`
  }

  if (firstAgg.sum) {
    return `sum by ${firstAgg.sum.field}`
  }

  if (firstAgg.avg) {
    return `avg by ${firstAgg.avg.field}`
  }

  if (firstAgg.value_count) {
    return `count by ${firstAgg.value_count.field}`
  }

  return 'aggregation'
}

function parseQueryString(queryStr) {
  if (!queryStr) return { match_all: {} }

  // Remove index prefix if present
  const cleanQuery = queryStr.replace(/^index:\w+\s*/, '').trim()
  
  if (!cleanQuery || cleanQuery === '*') {
    return { match_all: {} }
  }

  // Check for complex queries with boolean operators first
  if (cleanQuery.includes(' AND ') || cleanQuery.includes(' OR ') || cleanQuery.includes(' NOT ')) {
    return {
      query_string: {
        query: cleanQuery
      }
    }
  }

  // Simple field:value parsing (only for single field:value pairs)
  const fieldValueMatch = cleanQuery.match(/^(\w+):(.+)$/)
  if (fieldValueMatch) {
    const [, field, value] = fieldValueMatch

    // Check if it's a wildcard
    if (value.includes('*') || value.includes('?')) {
      return {
        wildcard: {
          [field]: value
        }
      }
    }

    // Check if it's a range
    if (value.includes('>=') || value.includes('<=') || value.includes('>') || value.includes('<')) {
      const range = {}
      if (value.includes('>=')) {
        range.gte = value.split('>=')[1].trim()
      } else if (value.includes('>')) {
        range.gt = value.split('>')[1].trim()
      }
      if (value.includes('<=')) {
        range.lte = value.split('<=')[1].trim()
      } else if (value.includes('<')) {
        range.lt = value.split('<')[1].trim()
      }

      return {
        range: {
          [field]: range
        }
      }
    }

    // Default to term query
    return {
      term: {
        [field]: value
      }
    }
  }

  // Fallback to query_string
  return {
    query_string: {
      query: cleanQuery
    }
  }
}

function parseAggregationString(aggStr) {
  if (!aggStr) return null

  const topMatch = aggStr.match(/^top by (\w+)/)
  if (topMatch) {
    return {
      top_terms: {
        terms: {
          field: topMatch[1],
          size: 10
        }
      }
    }
  }

  const timechartMatch = aggStr.match(/^timechart by (\w+)/)
  if (timechartMatch) {
    return {
      time_histogram: {
        date_histogram: {
          field: timechartMatch[1],
          calendar_interval: '1h'
        }
      }
    }
  }

  return null
}

function validateQueryStructure(query) {
  const result = { isValid: true, errors: [] }
  
  if (!query || typeof query !== 'object') {
    result.isValid = false
    result.errors.push('Query must be an object')
    return result
  }

  const validQueryTypes = ['match', 'term', 'terms', 'range', 'bool', 'match_all', 'wildcard', 'exists', 'query_string']
  const queryKeys = Object.keys(query)
  
  if (queryKeys.length === 0) {
    result.isValid = false
    result.errors.push('Query cannot be empty')
    return result
  }

  const hasValidType = queryKeys.some(key => validQueryTypes.includes(key))
  if (!hasValidType) {
    result.isValid = false
    result.errors.push(`Query must contain one of: ${validQueryTypes.join(', ')}`)
  }

  return result
}

function validateAggregationsStructure(aggs) {
  const result = { isValid: true, errors: [] }
  
  if (!aggs || typeof aggs !== 'object') {
    result.isValid = false
    result.errors.push('Aggregations must be an object')
    return result
  }

  const validAggTypes = ['terms', 'date_histogram', 'sum', 'avg', 'max', 'min', 'value_count', 'cardinality']
  
  for (const [aggName, aggDef] of Object.entries(aggs)) {
    if (!aggDef || typeof aggDef !== 'object') {
      result.isValid = false
      result.errors.push(`Aggregation "${aggName}" must be an object`)
      continue
    }

    const aggKeys = Object.keys(aggDef).filter(key => key !== 'aggs')
    const hasValidType = aggKeys.some(key => validAggTypes.includes(key))
    
    if (!hasValidType) {
      result.isValid = false
      result.errors.push(`Aggregation "${aggName}" must contain one of: ${validAggTypes.join(', ')}`)
    }
  }

  return result
}
